import { useBasePath } from '../../lib/use-base-path';
import { useLeaveChatOnNavigate } from '../../lib/use-leave-chat';
import { useTranslation } from '../../lib/i18n';
import { Tabs } from './ui';
import styles from './WorkspaceTabBar.module.css';

// A workspace is ONE screen with tabs (native `Route.workspaceHome(slug:tab:)`), but web models its
// tabs as sibling routes. Switching tabs therefore navigates with `replace`, not push: the history
// entry (and its `history.state.key`) is kept, so the per-navigation from-drawer flag carries across
// tabs — a workspace opened from the drawer keeps its MENU header instead of flipping to BACK — and
// back exits the workspace rather than stepping through tab history, matching native.
//
// **Two tabs, not three.** "Agents at work" and "Grow & Goals" asked the same question twice: both
// were a way into the Space's work. Dashboard is now the merge — the Routines / Tasks / Files summary
// cards over the Space's growth dashboard — and Goals keeps its own tab. `/workspace/[slug]/agents`
// still resolves, as a redirect here, so shipped links and deeplinks keep working.
//
// The strip itself is the DS `<Tabs>` (Figma "⚠️ Symphony DS" → Tabs Group), passing an `href` per
// item so each tab stays the real route it is — the primitive then renders the group as navigation
// links with `aria-current="page"`, which is what this bar always did.
export type WorkspaceTab = 'dashboard' | 'goals';

export function WorkspaceTabBar({ slug, active }: { slug: string; active: WorkspaceTab }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const encoded = encodeURIComponent(slug);

  return (
    <Tabs<WorkspaceTab>
      className={styles.tabs}
      // Names the landmark after the section it moves around in, using an existing translated key —
      // "Dashboard" would name the first tab rather than the group.
      label={t('sidebar.workspaces')}
      value={active}
      items={[
        {
          value: 'dashboard',
          label: t('workspace.nav.dashboard'),
          href: { pathname: '/workspace/[slug]', query: { slug } },
          linkAs: `${basePath}/workspace/${encoded}`,
          replace: true,
          onClick: leaveChat,
        },
        {
          value: 'goals',
          label: t('workspace.nav.goals'),
          href: { pathname: '/workspace/[slug]/goals', query: { slug } },
          linkAs: `${basePath}/workspace/${encoded}/goals`,
          replace: true,
          onClick: leaveChat,
        },
      ]}
    />
  );
}
